import { useMemo } from "react";
import { ChatMessage } from "../interfaces/copilot-chat.interfaces";
import { EndpointData, PageContext } from "./useCurrentPageContext";

export interface ChatContext {
  messageId?: string;
  messageIndex: number;
  timestamp?: string;
  pageContext: PageContext;
  attachedEndpoints: EndpointData[];
  description?: string;
}

export interface GroupedChatContext {
  contexts: ChatContext[];
  allEndpoints: EndpointData[];
  uniqueEndpointsByFullEndpoint: Map<string, EndpointData>;
}

/**
 * Hook that tracks context from all user messages with attachments in the chat
 * Extracts PageContext and endpoint information from grounding instructions
 */
export const useChatContextTracker = (messages: ChatMessage[]): GroupedChatContext => {
  return useMemo(() => {
  const contexts: ChatContext[] = [];
    const allEndpoints: EndpointData[] = [];
    const uniqueEndpointsByFullEndpoint = new Map<string, EndpointData>();
    const endpointMessageIndexMap = new Map<string, number>(); // Track which message index each endpoint came from

    // Process all user messages to extract context
    messages.forEach((message, index) => {
      if (message.role === "user" && message.hasAttachment && message.attachmentData?.groundingInstructions) {
        try {
          // Parse the grounding instructions to get the PageContext
          const groundingInstructions: {
            pageName?: string;
            tabName?: string;
            urlFilters?: Record<string, unknown>;
            attachedEndpoints?: EndpointData[];
          } = JSON.parse(message.attachmentData.groundingInstructions);

          // Extract PageContext information
          const pageContext: PageContext = {
            pageName: groundingInstructions.pageName || "Unknown",
            tabName: groundingInstructions.tabName || "",
            urlFilters: groundingInstructions.urlFilters || {},
            endpointData: groundingInstructions.attachedEndpoints || []
          };

          // Track the context for this message
          const chatContext: ChatContext = {
            messageId: message.id,
            messageIndex: index,
            timestamp: new Date().toISOString(), // Use current time as fallback, but message index is the primary indicator
            pageContext,
            attachedEndpoints: groundingInstructions.attachedEndpoints || [],
            description: message.attachmentData.description
          };

          contexts.push(chatContext);

          // Add all endpoints to our collections
          const messageEndpoints = groundingInstructions.attachedEndpoints || [];
          messageEndpoints.forEach((endpoint: EndpointData) => {
            // Add to all endpoints list
            allEndpoints.push(endpoint);

            // For deduplication, prioritize newer messages or different data content
            const existingEndpoint = uniqueEndpointsByFullEndpoint.get(endpoint.fullEndpoint);
            const existingMessageIndex = endpointMessageIndexMap.get(endpoint.fullEndpoint);

            const shouldUpdate = !existingEndpoint ||
              index > (existingMessageIndex || 0) ||
              (existingEndpoint && JSON.stringify(existingEndpoint.data) !== JSON.stringify(endpoint.data));

            if (shouldUpdate) {
              uniqueEndpointsByFullEndpoint.set(endpoint.fullEndpoint, endpoint);
              endpointMessageIndexMap.set(endpoint.fullEndpoint, index);
            }
          });
        } catch (error) {
          console.warn(`Failed to parse grounding instructions for message ${index}:`, error);
        }
      }
    });

    return {
      contexts,
      allEndpoints,
      uniqueEndpointsByFullEndpoint
    };
  }, [messages]);
};

/**
 * Helper function to check if a specific endpoint is already present in the chat
 */
export const isEndpointAlreadyAttached = (
  endpoint: EndpointData,
  trackedContext: GroupedChatContext
): boolean => {
  return trackedContext.uniqueEndpointsByFullEndpoint.has(endpoint.fullEndpoint);
};

/**
 * Helper function to get all endpoint descriptions that are already present in the chat
 */
export const getAttachedEndpointDescriptions = (
  trackedContext: GroupedChatContext
): string[] => {
  return Array.from(trackedContext.uniqueEndpointsByFullEndpoint.values())
    .map(endpoint => endpoint.dataExtractor.description)
    .filter(description => description && description.trim() !== "");
};